import React, { useEffect, useMemo, useState } from "react";
import { Alert, Box, Button, Paper, Stack, Typography } from "@mui/material";
import { useNavigate } from "react-router-dom";
import { apiFetch } from "../api/apiFetch";
import WeekSelector from "../features/weeklySummary/components/WeekSelector";
import WeekOverviewStats from "../features/weeklySummary/components/WeekOverviewStats";
import HighlightList from "../features/weeklySummary/components/HighlightList";
import KeyActionsList from "../features/weeklySummary/components/KeyActionsList";
import CircleActivitySummary from "../features/weeklySummary/components/CircleActivitySummary";
import LookingAheadCard from "../features/weeklySummary/components/LookingAheadCard";
import { buildWeeklySummaryModel } from "../features/weeklySummary/utils/buildWeeklySummaryModel";
import LoadingState from "../components/common/LoadingState";

export default function WeeklySummaryPage() {
  const [summary, setSummary] = useState(null);
  const [summaries, setSummaries] = useState([]);
  const [selectedSummaryId, setSelectedSummaryId] = useState("");
  const [loading, setLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState("");
  const navigate = useNavigate();

  useEffect(() => {
    let isMounted = true;

    async function loadSummaries() {
      setLoading(true);
      setErrorMessage("");
      try {
        const [current, history] = await Promise.all([
          apiFetch("/weekly-summaries/me/"),
          apiFetch("/weekly-summaries/me/history/"),
        ]);
        if (!isMounted) return;
        const combined = [...(current ? [current] : []), ...(Array.isArray(history) ? history : [])];
        const unique = new Map();
        combined.forEach((item) => {
          if (!item?.id) return;
          unique.set(String(item.id), item);
        });
        const list = Array.from(unique.values());
        setSummaries(list);
        setSummary(current || null);
        setSelectedSummaryId((prev) => prev || String(list[0]?.id || ""));
      } catch (error) {
        if (!isMounted) return;
        console.error("Failed to load weekly summary", error);
        setSummaries([]);
        setSummary(null);
        setErrorMessage("Unable to load weekly summary.");
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    }

    loadSummaries();
    return () => {
      isMounted = false;
    };
  }, []);

  const activeSummary = useMemo(() => {
    if (!selectedSummaryId) return summary;
    return summaries.find((item) => String(item.id) === String(selectedSummaryId)) || summary;
  }, [selectedSummaryId, summaries, summary]);

  const selectedIndex = summaries.findIndex((item) => String(item.id) === String(selectedSummaryId));
  const historyLocked = Boolean(summaries.some((item) => item?.history_locked));
  const model = useMemo(() => buildWeeklySummaryModel(activeSummary), [activeSummary]);

  function handleSelectSummary(id) {
    setSelectedSummaryId(String(id));
  }

  function handleStepSummary(direction) {
    if (selectedIndex < 0) return;
    const nextItem = summaries[selectedIndex + direction];
    if (nextItem) {
      setSelectedSummaryId(String(nextItem.id));
    }
  }

  if (loading && !activeSummary) {
    return (
      <Box sx={{ mt: 4, maxWidth: 720 }}>
        <LoadingState
          title="Preparing your weekly summary"
          body="Bringing together your check-ins, habits, and network signals."
          rows={4}
        />
      </Box>
    );
  }

  if (errorMessage && !activeSummary) {
    return (
      <Box sx={{ mt: 4 }}>
        <Alert severity="error">{errorMessage}</Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ maxWidth: 980, mx: "auto", mt: { xs: 2, md: 3 }, pb: 5 }}>
      <WeekSelector
        summaries={summaries}
        selectedId={selectedSummaryId}
        onSelect={handleSelectSummary}
        onPrevious={() => handleStepSummary(1)}
        onNext={() => handleStepSummary(-1)}
        hasPrevious={selectedIndex >= 0 && selectedIndex < summaries.length - 1}
        hasNext={selectedIndex > 0}
      />

      {historyLocked ? (
        <Typography variant="caption" sx={{ display: "block", mt: 1.2, color: "#7b8799" }}>
          Free includes this week and your latest completed week. Older weeks are in TrustBud Plus.
          {" "}
          <Button size="small" variant="text" onClick={() => navigate("/trustbud-plus?feature=weekly-summary")} sx={{ textTransform: "none", minWidth: 0, px: 0.5 }}>
            What Plus Unlocks
          </Button>
        </Typography>
      ) : null}

      <Paper
        elevation={0}
        sx={{
          mt: 2.2,
          borderRadius: "26px",
          p: { xs: 2, md: 2.5 },
          border: "1px solid #e6edf5",
          backgroundColor: "#ffffff",
          boxShadow: "0 16px 38px rgba(15, 23, 42, 0.06)",
        }}
      >
        <Typography variant="body2" sx={{ color: "#7a879c", fontWeight: 600 }}>
          {model.weekLabel}
        </Typography>
        <Typography variant="h5" sx={{ mt: 0.75, fontWeight: 800, color: "#13233d" }}>
          This week, in a few lines
        </Typography>
        <Typography variant="body1" sx={{ mt: 1.1, color: "#44566f" }}>
          {model.reinforcement}
        </Typography>

        <Box sx={{ mt: 2.1 }}>
          <WeekOverviewStats stats={model.stats} />
        </Box>
      </Paper>

      <Box sx={{ mt: 2.5 }}>
        <SectionTitle title="Highlights" />
        <HighlightList highlights={model.highlights} />
      </Box>

      {model.appliedChanges?.length ? (
        <Box sx={{ mt: 2.5 }}>
          <SectionTitle title={model.plusLearning.title} />
          <Paper
            elevation={0}
            sx={{
              mt: 1.1,
              borderRadius: "22px",
              p: 2.2,
              border: "1px solid #e8eef6",
              backgroundColor: "#ffffff",
            }}
          >
            <Stack spacing={1.6}>
              <Typography sx={{ fontSize: 14, color: "#44566f", fontWeight: 650 }}>
                {model.plusLearning.body}
              </Typography>
              {model.appliedChanges.map((change) => (
                <Box key={change.id}>
                  <Typography sx={{ fontSize: 15.5, fontWeight: 800, color: "#13233d" }}>
                    {change.title || "Support added"}
                  </Typography>
                  <Typography sx={{ mt: 0.35, fontSize: 14, color: "#44566f" }}>
                    {change.what_changed || change.message || "TrustBud added support."}
                  </Typography>
                  {change.result_detail ? (
                    <Typography sx={{ mt: 0.35, fontSize: 14, color: "#44566f" }}>
                      {change.result_detail}
                    </Typography>
                  ) : null}
                  <Stack direction="row" spacing={1} sx={{ mt: 0.9, flexWrap: "wrap" }}>
                    <Typography
                      component="span"
                      sx={{
                        px: 1.1,
                        py: 0.4,
                        borderRadius: 999,
                        bgcolor: "#eaf7ef",
                        color: "#166534",
                        fontSize: 12,
                        fontWeight: 800,
                      }}
                    >
                      {change.result_so_far || change.status_label || "Still learning"}
                    </Typography>
                    <Typography
                      component="span"
                      sx={{
                        px: 1.1,
                        py: 0.4,
                        borderRadius: 999,
                        bgcolor: "#eef4fb",
                        color: "#17345d",
                        fontSize: 12,
                        fontWeight: 800,
                      }}
                    >
                      {change.what_to_keep_or_change_next || "Keep checking in."}
                    </Typography>
                  </Stack>
                </Box>
              ))}
            </Stack>
          </Paper>
        </Box>
      ) : null}

      <Box sx={{ mt: 2.5 }}>
        <SectionTitle title="What you did most" />
        <Paper
          elevation={0}
          sx={{
            mt: 1.1,
            borderRadius: "22px",
            p: 2.2,
            border: "1px solid #e8eef6",
            backgroundColor: "#ffffff",
          }}
        >
          <KeyActionsList actions={model.keyActions} />
        </Paper>
      </Box>

      <Box sx={{ mt: 2.5 }}>
        <SectionTitle title="Around you" />
        <Box sx={{ mt: 1.1 }}>
          <CircleActivitySummary circle={model.circleActivity} />
        </Box>
      </Box>

      <Box sx={{ mt: 2.5 }}>
        <SectionTitle title="Looking ahead" />
        <Box sx={{ mt: 1.1 }}>
          <LookingAheadCard lookingAhead={model.lookingAhead} onPlanNextWeek={() => navigate("/plan")} />
        </Box>
      </Box>
    </Box>
  );
}

function SectionTitle({ title }) {
  return (
    <Stack spacing={0.3}>
      <Typography variant="h6" sx={{ fontWeight: 800, color: "#13233d" }}>
        {title}
      </Typography>
    </Stack>
  );
}
